/**
 * Decide a "forma" do formulário por loja — replica as condições de
 * `legacy/fixe-link-2/routes/invite.js` (`getInvite`), que escolhe entre
 * ~30 templates quase-idênticos por `passbook.slug`/flags. Em vez de 14
 * branches de template, um único resolver decide quais campos aparecem;
 * o resto (termos, regras de fidelidade, addFields) é sempre o mesmo.
 */

export type IdentityField = 'cpf' | 'cnpj' | 'car-plate' | 'intl-phone' | 'hidden-generated';

export interface InviteFieldPlan {
  identity: IdentityField;
  showBirthday: boolean;
  showGender: boolean;
  showStateCity: boolean;
  /** Locale só para textos do grupo internacional — não é i18n completo (ver intl-phone.ts). */
  locale: 'pt' | 'es' | 'en';
  /**
   * brincarte: os campos de identidade/nascimento/gênero são da CRIANÇA, não
   * de quem preenche — relabela com "da Criança" + separa em duas seções
   * ("Dados da Criança"/"Dados do Responsável"), sem mudar a estrutura de
   * dados (invite_brincarte.ejs usa exatamente os mesmos campos do genérico).
   */
  childLabels: boolean;
  /** circus: lista repetível de nomes de filhos, além dos dados do titular. */
  showChildrenGroup: boolean;
  /**
   * Renderiza o bloco de `addFields` (campos customizados do passbook) no
   * formulário público. A maioria dos templates legados TEM `addFields`
   * configurado no banco mas NUNCA o renderiza — confirmado via grep exato de
   * `label for="<%=fields_passbook[i].slug%>"` em todas as views `invite*.ejs`:
   * só 6 templates têm esse loop no corpo (os outros só citam
   * `fields_passbook` num snippet JS de máscara, que não renderiza nada).
   */
  showCustomFields: boolean;
}

/** slug === 'ciadamacaxeiraclub' → invite_ciadamacaxeirab2b.ejs */
const CNPJ_SLUGS = new Set(['ciadamacaxeiraclub']);
/** slug === 'goflashwash' → invite_goflashwash.ejs (mostra addFields) */
const CAR_PLATE_SLUGS = new Set(['goflashwash']);
/** invite_ardevilla.ejs */
const PT_SLUGS = new Set([
  'ardevilla',
  'fixept',
  'fixecrmpt',
  'realracingclub',
  'caffecaffe',
  'areluna',
  'splashporto',
]);
/** invite_vipcell.ejs */
const STATE_CITY_SLUGS = new Set(['vipcell', 'nathansfamous', 'teashop']);
/** invite_abf.ejs (mostra addFields) */
const ABF_SLUGS = new Set(['abf', 'sqg']);
/** invite_brincarte.ejs — mesmos campos do genérico, só relabelados (mostra addFields). */
const BRINCARTE_SLUGS = new Set(['brincarte']);
/** invite_circus.ejs */
const CIRCUS_SLUGS = new Set(['circus']);
/** invite_emporiobrownie.ejs (mostra addFields — ex.: "Loja Favorita") */
const EMPORIOBROWNIE_SLUGS = new Set(['emporiobrownie']);
/** invite_lavessel.ejs (mostra addFields) */
const LAVESSEL_SLUGS = new Set(['lavessel', 'd1like', 'riplaygranja', 'frappe']);
/** invite_bubblemix.ejs (mostra addFields + aviso de validação por WhatsApp) */
const BUBBLEMIX_SLUGS = new Set(['bubblemix', 'bbmtreinamento']);
/** Formulário genérico + addFields (sem template legado próprio) — ex.: barre (CEP). */
const GENERIC_CUSTOM_FIELDS_SLUGS = new Set(['barre']);

/** Passbooks que não pedem gênero no cadastro (pedido do cliente, sem template legado próprio). */
const HIDE_GENDER_PASSBOOK_IDS = new Set(['6ab32680477cc22542b578c5']);

interface PassbookFlags {
  idPassbook?: string;
  slug?: string;
  hasPT?: boolean;
  hasES?: boolean;
  hasIN?: boolean;
}

const BASE_PLAN: Omit<InviteFieldPlan, 'identity'> = {
  showBirthday: true,
  showGender: true,
  showStateCity: false,
  locale: 'pt',
  childLabels: false,
  showChildrenGroup: false,
  showCustomFields: false,
};

export function resolveInviteFieldPlan(passbook: PassbookFlags): InviteFieldPlan {
  const plan = resolveBasePlan(passbook);
  if (HIDE_GENDER_PASSBOOK_IDS.has(passbook.idPassbook ?? '')) {
    return { ...plan, showGender: false };
  }
  return plan;
}

function resolveBasePlan(passbook: PassbookFlags): InviteFieldPlan {
  const slug = passbook.slug ?? '';

  if (ABF_SLUGS.has(slug)) {
    return {
      ...BASE_PLAN,
      identity: 'hidden-generated',
      showBirthday: false,
      showGender: false,
      showCustomFields: true,
    };
  }
  if (CNPJ_SLUGS.has(slug)) {
    return { ...BASE_PLAN, identity: 'cnpj' };
  }
  if (CAR_PLATE_SLUGS.has(slug)) {
    return { ...BASE_PLAN, identity: 'car-plate', showCustomFields: true };
  }
  if (PT_SLUGS.has(slug) || passbook.hasPT) {
    return { ...BASE_PLAN, identity: 'intl-phone', locale: 'pt' };
  }
  if (passbook.hasES) {
    return { ...BASE_PLAN, identity: 'intl-phone', locale: 'es' };
  }
  if (slug === 'demofixept' || passbook.hasIN) {
    return { ...BASE_PLAN, identity: 'intl-phone', locale: 'en' };
  }
  if (STATE_CITY_SLUGS.has(slug)) {
    return { ...BASE_PLAN, identity: 'cpf', showStateCity: true };
  }
  if (BRINCARTE_SLUGS.has(slug)) {
    return { ...BASE_PLAN, identity: 'cpf', childLabels: true, showCustomFields: true };
  }
  if (CIRCUS_SLUGS.has(slug)) {
    return { ...BASE_PLAN, identity: 'cpf', showChildrenGroup: true };
  }
  if (
    EMPORIOBROWNIE_SLUGS.has(slug) ||
    LAVESSEL_SLUGS.has(slug) ||
    BUBBLEMIX_SLUGS.has(slug) ||
    GENERIC_CUSTOM_FIELDS_SLUGS.has(slug)
  ) {
    return { ...BASE_PLAN, identity: 'cpf', showCustomFields: true };
  }
  return { ...BASE_PLAN, identity: 'cpf' };
}

export function isBubblemixSlug(slug?: string): boolean {
  return BUBBLEMIX_SLUGS.has(slug ?? '');
}

/**
 * Cartão-presente (Fase 4): `passbook.hasGiftcard` muda o formulário inteiro
 * pra outro fluxo (sem CPF/nascimento/gênero, endpoints `/auth/registerGift`
 * em vez de `/auth/register`) — por isso é resolvido separado do
 * `InviteFieldPlan` normal, não composto com ele.
 *
 * - `omatuto`: cadastro normal de cartão-presente (cria um cartão novo).
 * - `trintaeum`/`trintaeum-livre`: RESGATE de um cartão já pré-carregado no
 *   sistema (nunca cria do zero) — usuário digita o código impresso no
 *   cartão físico; `trintaeum-livre` também deixa escolher o valor a
 *   carregar (`trintaeum` tem valor fixo definido no backend).
 */
export type GiftVariant = 'none' | 'omatuto' | 'trintaeum' | 'trintaeum-livre';

/** invite_trintaeum.ejs */
const TRINTAEUM_SLUGS = new Set(['broa', 'trintaeumcafedamanha', 'trintaeumdegustacao']);
/** invite_trintaeum_livre.ejs */
const TRINTAEUM_LIVRE_SLUGS = new Set(['trintaeumcozinhamineira']);

export function resolveGiftVariant(passbook: { hasGiftcard?: boolean; slug?: string }): GiftVariant {
  if (!passbook.hasGiftcard) return 'none';
  const slug = passbook.slug ?? '';
  if (TRINTAEUM_SLUGS.has(slug)) return 'trintaeum';
  if (TRINTAEUM_LIVRE_SLUGS.has(slug)) return 'trintaeum-livre';
  return 'omatuto';
}
